/**
 * Pool settings fields
 *
 * The settings every linked gift match shares: name, multiplier, donation details, image, on/off
 * and conditions.
 */

import React from 'react';
import { Checkbox, Dropdown, Form } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { Controller, useFormContext } from 'react-hook-form';
import { ImageUploadInput } from '../../../../components/form/imageUploadInput';
import {
  entityTypes,
  getFullImagePathByType,
  uploadTypes
} from '../../../../constants/imageUpload';
import {
  donationFromMaxLength,
  donationMessageMaxLength,
  giftMatchingKeys,
  i18nKeys,
  maxMultiplier,
  nameMaxLength
} from '../constants';
import { PoolFormValues } from '../types';
import PoolConditionsFields from './PoolConditionsFields';

const multiplierOptions = Array.from({ length: maxMultiplier }, (_, i) => ({
  key: `multiplier-${i + 1}`,
  text: `${i + 1}`,
  value: i + 1
}));

interface PoolSettingsFieldsProps {
  conditionErrorKeys: string[];
  idPrefix: string;
}

const PoolSettingsFields: React.FC<PoolSettingsFieldsProps> = ({
  conditionErrorKeys,
  idPrefix
}) => {
  const { control, formState, register } = useFormContext<PoolFormValues>();
  const { errors } = formState;

  return (
    <>
      <Form.Field required error={!!errors.name}>
        <label htmlFor={`${idPrefix}-name`}>
          <Translate value={giftMatchingKeys.NAME} />
        </label>
        <input
          id={`${idPrefix}-name`}
          maxLength={nameMaxLength}
          {...register('name', {
            validate: (value: string) => !!value?.trim()
          })}
        />
        <p className="field-description">
          <Translate value={giftMatchingKeys.NAME_SUBTEXT} />
        </p>
      </Form.Field>

      <Form.Field required>
        <label htmlFor={`${idPrefix}-multiplier`}>
          <Translate value={giftMatchingKeys.MULTIPLIER} />
        </label>
        <Controller
          name="multiplier"
          control={control}
          render={({ field }) => (
            <Dropdown
              id={`${idPrefix}-multiplier`}
              aria-label={I18n.t(giftMatchingKeys.MULTIPLIER)}
              selection
              options={multiplierOptions}
              value={field.value}
              onChange={(_e, data) => field.onChange(Number(data.value))}
            />
          )}
        />
        <p className="field-description">
          <Translate value={giftMatchingKeys.MULTIPLIER_SUBTEXT} />
        </p>
      </Form.Field>

      <Form.Field>
        <label htmlFor={`${idPrefix}-donation-from`}>
          <Translate value={giftMatchingKeys.DONATION_FROM} />
        </label>
        <input
          id={`${idPrefix}-donation-from`}
          maxLength={donationFromMaxLength}
          {...register('donationFrom')}
        />
        <p className="field-description">
          <Translate value={giftMatchingKeys.DONATION_FROM_SUBTEXT} />
        </p>
      </Form.Field>

      <Form.Field>
        <label htmlFor={`${idPrefix}-donation-message`}>
          <Translate value={giftMatchingKeys.DONATION_MESSAGE} />
        </label>
        <textarea
          id={`${idPrefix}-donation-message`}
          rows={2}
          maxLength={donationMessageMaxLength}
          {...register('donationMessage')}
        />
        <p className="field-description">
          <Translate value={giftMatchingKeys.DONATION_MESSAGE_SUBTEXT} />
        </p>
      </Form.Field>

      <Controller
        name="mainImagePath"
        control={control}
        render={({ field }) => (
          <ImageUploadInput
            label={I18n.t(giftMatchingKeys.IMAGE)}
            subtext={giftMatchingKeys.IMAGE_SUBTEXT}
            url={
              field.value
                ? getFullImagePathByType(field.value, uploadTypes.LOGO) || ''
                : ''
            }
            position="left"
            height={350}
            width={350}
            uploadType={uploadTypes.LOGO}
            entity={entityTypes.CAMPAIGN}
            onChange={(url?: string) => field.onChange(url ?? null)}
          />
        )}
      />

      <Form.Field>
        <Controller
          name="enabled"
          control={control}
          render={({ field }) => (
            <Checkbox
              id={`${idPrefix}-enabled`}
              toggle
              label={I18n.t(i18nKeys.FIELDS.ENABLED)}
              checked={!!field.value}
              onChange={(_e, data) => field.onChange(!!data.checked)}
            />
          )}
        />
        <p className="field-description">
          <Translate value={i18nKeys.FIELDS.ENABLED_HELP} />
        </p>
      </Form.Field>

      <PoolConditionsFields
        errorKeys={conditionErrorKeys}
        idPrefix={idPrefix}
      />
    </>
  );
};

export default PoolSettingsFields;
